import React, { useState, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import Card from '../common/Card';
import TutoresForm from './TutoresForm';
import DocumentosModal from './DocumentosModal';
import { validarCI, validarEdadMinima, validarCuposDisponibles, validarFormularioRegistro } from '../../utils/validators';
import registroService from '../../services/registroService';

/**
 * Componente principal para el registro de estudiantes
 * Flujo de múltiples pasos con validaciones progresivas
 */
const RegistroEstudianteForm = ({ onRegistroExitoso }) => {
  const [paso, setPaso] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);
  const [numerosRegistro, setNumerosRegistro] = useState(null);

  // Datos del formulario
  const [datosEstudiante, setDatosEstudiante] = useState({
    ci: '',
    nombre: '',
    apellido: '',
    fecha_nacimiento: '',
    sexo: '',
    celular: '',
    direccion: '',
    numero_documento: '',
    observaciones: ''
  });

  const [datosNivel, setDatosNivel] = useState({
    nivel: '',
    curso_id: '',
    paralelo_id: ''
  });

  const [tutores, setTutores] = useState([]);
  const [documentosCargados, setDocumentosCargados] = useState([]);

  // Datos para dropdowns
  const [niveles, setNiveles] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [paralelos, setParalelos] = useState([]);
  const [infoParalelo, setInfoParalelo] = useState(null);
  const [mostrarDocumentos, setMostrarDocumentos] = useState(false);

  // Errores
  const [erroresEstudiante, setErroresEstudiante] = useState({});
  const [erroresNivel, setErroresNivel] = useState({});
  const [erroresTutores, setErroresTutores] = useState({});

  // Cargar niveles al iniciar
  useEffect(() => {
    const cargarNiveles = async () => {
      try {
        setCargando(true);
        const response = await registroService.getNiveles();
        if (response.success) {
          setNiveles(response.data.map(n => ({
            value: n.id,
            label: n.nombre
          })));
        }
      } catch (err) {
        console.error('Error cargando niveles:', err);
        setError('Error al cargar los niveles educativos');
      } finally {
        setCargando(false);
      }
    };

    cargarNiveles();
  }, []);

  // Cargar cursos cuando cambia el nivel
  useEffect(() => {
    if (!datosNivel.nivel) {
      setCursos([]);
      setDatosNivel(prev => ({ ...prev, curso_id: '', paralelo_id: '' }));
      return;
    }

    const cargarCursos = async () => {
      try {
        setCargando(true);
        const response = await registroService.getCursosPorNivel(datosNivel.nivel);
        if (response.success) {
          setCursos(response.data.map(c => ({
            value: c.id,
            label: c.nombre
          })));
          setDatosNivel(prev => ({ ...prev, curso_id: '', paralelo_id: '' }));
        }
      } catch (err) {
        console.error('Error cargando cursos:', err);
        setError('Error al cargar los cursos');
      } finally {
        setCargando(false);
      }
    };

    cargarCursos();
  }, [datosNivel.nivel]);

  // Cargar paralelos cuando cambia el curso
  useEffect(() => {
    if (!datosNivel.curso_id) {
      setParalelos([]);
      setDatosNivel(prev => ({ ...prev, paralelo_id: '' }));
      setInfoParalelo(null);
      return;
    }

    const cargarParalelos = async () => {
      try {
        setCargando(true);
        const response = await registroService.getParalelosPorCurso(datosNivel.curso_id);
        if (response.success) {
          setParalelos(response.data.map(p => ({
            value: p.id,
            label: `${p.nombre} (${p.cupo_disponible}/${p.cupo_total} cupos disponibles)`
          })));
          setDatosNivel(prev => ({ ...prev, paralelo_id: '' }));
        }
      } catch (err) {
        console.error('Error cargando paralelos:', err);
        setError('Error al cargar los paralelos');
      } finally {
        setCargando(false);
      }
    };

    cargarParalelos();
  }, [datosNivel.curso_id]);

  // Validar disponibilidad cuando se selecciona un paralelo
  useEffect(() => {
    if (!datosNivel.paralelo_id) {
      setInfoParalelo(null);
      return;
    }

    const validarDisponibilidad = async () => {
      try {
        const response = await registroService.validarDisponibilidad(datosNivel.paralelo_id);
        if (response.success) {
          setInfoParalelo({
            disponible: response.disponible,
            cupos_disponibles: response.cupos_disponibles,
            cupos_totales: response.cupos_totales,
            porcentaje_ocupacion: response.porcentaje_ocupacion
          });
        }
      } catch (err) {
        console.error('Error validando disponibilidad:', err);
      }
    };

    validarDisponibilidad();
  }, [datosNivel.paralelo_id]);

  // Validar paso 1 (Datos del estudiante)
  const validarPaso1 = useCallback(() => {
    const errores = {};

    if (!datosEstudiante.ci?.trim()) {
      errores.ci = 'La cédula es obligatoria';
    } else {
      const errorCI = validarCI(datosEstudiante.ci);
      if (errorCI) errores.ci = errorCI;
    }

    if (!datosEstudiante.nombre?.trim()) {
      errores.nombre = 'El nombre es obligatorio';
    } else if (datosEstudiante.nombre.length > 100) {
      errores.nombre = 'El nombre no puede exceder 100 caracteres';
    }

    if (!datosEstudiante.apellido?.trim()) {
      errores.apellido = 'El apellido es obligatorio';
    } else if (datosEstudiante.apellido.length > 100) {
      errores.apellido = 'El apellido no puede exceder 100 caracteres';
    }

    if (!datosEstudiante.fecha_nacimiento) {
      errores.fecha_nacimiento = 'La fecha de nacimiento es obligatoria';
    } else {
      const errorEdad = validarEdadMinima(datosEstudiante.fecha_nacimiento, 3);
      if (errorEdad) errores.fecha_nacimiento = errorEdad;
    }

    if (!datosEstudiante.sexo) {
      errores.sexo = 'El sexo es obligatorio';
    }

    if (!datosEstudiante.direccion?.trim()) {
      errores.direccion = 'La dirección es obligatoria';
    }

    setErroresEstudiante(errores);
    return Object.keys(errores).length === 0;
  }, [datosEstudiante]);

  // Validar paso 2 (Nivel y curso)
  const validarPaso2 = useCallback(() => {
    const errores = {};

    if (!datosNivel.nivel) {
      errores.nivel = 'Debe seleccionar un nivel';
    }

    if (!datosNivel.curso_id) {
      errores.curso_id = 'Debe seleccionar un curso';
    }

    if (!datosNivel.paralelo_id) {
      errores.paralelo_id = 'Debe seleccionar un paralelo';
    }

    if (infoParalelo && !infoParalelo.disponible) {
      errores.paralelo_id = 'No hay cupos disponibles en este paralelo';
    }

    setErroresNivel(errores);
    return Object.keys(errores).length === 0;
  }, [datosNivel, infoParalelo]);

  // Validar paso 3 (Tutores)
  const validarPaso3 = useCallback(() => {
    const errores = {};

    if (tutores.length === 0) {
      errores.tutores = 'Debe registrar al menos un tutor';
      setErroresTutores(errores);
      return false;
    }

    const tienePrincipal = tutores.some(t => t.es_contacto_principal === true);
    if (!tienePrincipal) {
      errores.tutores = 'Debe seleccionar un tutor como contacto principal';
      setErroresTutores(errores);
      return false;
    }

    setErroresTutores({});
    return true;
  }, [tutores]);

  // Cambiar datos del estudiante
  const cambiarDatosEstudiante = useCallback((campo, valor) => {
    setDatosEstudiante(prev => ({
      ...prev,
      [campo]: valor
    }));
    // Limpiar error del campo
    if (erroresEstudiante[campo]) {
      setErroresEstudiante(prev => {
        const nuevo = { ...prev };
        delete nuevo[campo];
        return nuevo;
      });
    }
  }, [erroresEstudiante]);

  // Cambiar datos de nivel
  const cambiarDatosNivel = useCallback((campo, valor) => {
    setDatosNivel(prev => ({
      ...prev,
      [campo]: valor
    }));
    // Limpiar error del campo
    if (erroresNivel[campo]) {
      setErroresNivel(prev => {
        const nuevo = { ...prev };
        delete nuevo[campo];
        return nuevo;
      });
    }
  }, [erroresNivel]);

  // Navegar al siguiente paso
  const irAlSiguiente = useCallback(() => {
    if (paso === 1 && !validarPaso1()) return;
    if (paso === 2 && !validarPaso2()) return;
    if (paso === 3 && !validarPaso3()) return;

    setPaso(paso + 1);
    setError(null);
  }, [paso, validarPaso1, validarPaso2, validarPaso3]);

  // Retroceder
  const irAlAnterior = useCallback(() => {
    setPaso(paso - 1);
    setError(null);
  }, [paso]);

  // Enviar formulario
  const enviarFormulario = async () => {
    // Validaciones finales
    if (!validarPaso1()) return;
    if (!validarPaso2()) return;
    if (!validarPaso3()) return;

    setEnviando(true);
    setError(null);

    try {
      const datosRegistro = {
        ...datosEstudiante,
        paralelo_id: parseInt(datosNivel.paralelo_id),
        fecha_ingreso: new Date().toISOString().split('T')[0],
        tutores: tutores.map(t => ({
          nombre: t.nombre,
          apellido: t.apellido,
          email: t.email,
          telefono: t.telefono,
          relacion: t.relacion,
          ocupacion: t.ocupacion,
          es_contacto_principal: t.es_contacto_principal
        }))
      };

      const response = await registroService.registrarEstudiante(datosRegistro);

      if (response.success) {
        setExito(true);
        setNumerosRegistro(response.data.registro_estudiante);
        
        // Limpiar formulario
        setTimeout(() => {
          if (onRegistroExitoso) {
            onRegistroExitoso(response.data);
          }
        }, 3000);
      } else {
        setError(response.message || 'Error al registrar el estudiante');
      }
    } catch (err) {
      console.error('Error en registro:', err);
      setError(err.response?.data?.message || 'Error al registrar el estudiante. Por favor intente nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  // Pantalla de éxito
  if (exito) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <Card className="max-w-md w-full">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle size={64} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              ¡Registro Exitoso!
            </h2>
            <p className="text-gray-600">
              El estudiante ha sido registrado correctamente en el sistema.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Número de Registro:</strong>
              </p>
              <p className="text-lg font-mono font-bold text-green-700">
                {numerosRegistro}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              Por favor guarde este número para futuras referencias.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Encabezado */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Registro de Estudiantes
          </h1>
          <p className="text-gray-600">
            Complete el formulario para registrar un nuevo estudiante en el colegio
          </p>
        </div>

        {/* Indicador de progreso */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Paso {paso} de 4
            </span>
            <span className="text-sm font-medium text-gray-600">
              {Math.round((paso / 4) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
              style={{ width: `${(paso / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Errores generales */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Contenido según el paso */}
        <Card className="mb-8">
          {cargando && paso === 1 ? (
            <div className="flex items-center justify-center py-12">
              <Loader size={32} className="animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* PASO 1: Datos del estudiante */}
              {paso === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      Datos Personales del Estudiante
                    </h2>
                    <p className="text-sm text-gray-600">
                      Ingrese la información básica del estudiante
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cédula de Identidad *
                      </label>
                      <Input
                        type="text"
                        value={datosEstudiante.ci}
                        onChange={(e) => cambiarDatosEstudiante('ci', e.target.value)}
                        placeholder="Ej: 1234567"
                        error={erroresEstudiante.ci}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre *
                      </label>
                      <Input
                        type="text"
                        value={datosEstudiante.nombre}
                        onChange={(e) => cambiarDatosEstudiante('nombre', e.target.value)}
                        placeholder="Ej: Juan"
                        error={erroresEstudiante.nombre}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apellido *
                      </label>
                      <Input
                        type="text"
                        value={datosEstudiante.apellido}
                        onChange={(e) => cambiarDatosEstudiante('apellido', e.target.value)}
                        placeholder="Ej: Pérez"
                        error={erroresEstudiante.apellido}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de Nacimiento *
                      </label>
                      <Input
                        type="date"
                        value={datosEstudiante.fecha_nacimiento}
                        onChange={(e) => cambiarDatosEstudiante('fecha_nacimiento', e.target.value)}
                        error={erroresEstudiante.fecha_nacimiento}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sexo *
                      </label>
                      <Select
                        value={datosEstudiante.sexo}
                        onChange={(e) => cambiarDatosEstudiante('sexo', e.target.value)}
                        options={[
                          { value: 'M', label: 'Masculino' },
                          { value: 'F', label: 'Femenino' }
                        ]}
                        error={erroresEstudiante.sexo}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono/Celular
                      </label>
                      <Input
                        type="tel"
                        value={datosEstudiante.celular}
                        onChange={(e) => cambiarDatosEstudiante('celular', e.target.value)}
                        placeholder="Ej: 0987654321"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dirección de Domicilio *
                    </label>
                    <Input
                      type="text"
                      value={datosEstudiante.direccion}
                      onChange={(e) => cambiarDatosEstudiante('direccion', e.target.value)}
                      placeholder="Ej: Av. Principal 123"
                      error={erroresEstudiante.direccion}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Documento (Opcional)
                    </label>
                    <Input
                      type="text"
                      value={datosEstudiante.numero_documento}
                      onChange={(e) => cambiarDatosEstudiante('numero_documento', e.target.value)}
                      placeholder="Ej: ABC123"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observaciones (Opcional)
                    </label>
                    <textarea
                      value={datosEstudiante.observaciones}
                      onChange={(e) => cambiarDatosEstudiante('observaciones', e.target.value)}
                      placeholder="Información adicional relevante..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows="3"
                    />
                  </div>
                </div>
              )}

              {/* PASO 2: Selección de nivel, curso y paralelo */}
              {paso === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      Seleccione Nivel, Curso y Paralelo
                    </h2>
                    <p className="text-sm text-gray-600">
                      Elija el nivel educativo y el curso donde será inscrito el estudiante
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nivel Educativo *
                      </label>
                      <Select
                        value={datosNivel.nivel}
                        onChange={(e) => cambiarDatosNivel('nivel', e.target.value)}
                        options={niveles}
                        error={erroresNivel.nivel}
                        loading={cargando}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Curso *
                      </label>
                      <Select
                        value={datosNivel.curso_id}
                        onChange={(e) => cambiarDatosNivel('curso_id', e.target.value)}
                        options={cursos}
                        error={erroresNivel.curso_id}
                        disabled={!datosNivel.nivel}
                        loading={cargando && datosNivel.nivel && cursos.length === 0}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Paralelo *
                    </label>
                    <Select
                      value={datosNivel.paralelo_id}
                      onChange={(e) => cambiarDatosNivel('paralelo_id', e.target.value)}
                      options={paralelos}
                      error={erroresNivel.paralelo_id}
                      disabled={!datosNivel.curso_id}
                      loading={cargando && datosNivel.curso_id && paralelos.length === 0}
                    />
                  </div>

                  {/* Información del paralelo */}
                  {infoParalelo && (
                    <div className={`p-4 rounded-lg border-2 ${
                      infoParalelo.disponible
                        ? 'border-green-200 bg-green-50'
                        : 'border-red-200 bg-red-50'
                    }`}>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 uppercase">Cupos Disponibles</p>
                          <p className={`text-2xl font-bold ${
                            infoParalelo.disponible ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {infoParalelo.cupos_disponibles}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 uppercase">Cupo Total</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {infoParalelo.cupos_totales}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 uppercase">Ocupación</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {infoParalelo.porcentaje_ocupacion}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PASO 3: Tutores */}
              {paso === 3 && (
                <TutoresForm
                  tutores={tutores}
                  onTutoresChange={setTutores}
                  errors={erroresTutores}
                />
              )}

              {/* PASO 4: Resumen y documentos */}
              {paso === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                      Resumen del Registro
                    </h2>
                    <p className="text-sm text-gray-600">
                      Verifique que todos los datos sean correctos
                    </p>
                  </div>

                  {/* Resumen datos estudiante */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 space-y-2">
                    <h3 className="font-medium text-gray-900">Datos del Estudiante</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-600">Cédula:</p>
                        <p className="font-medium text-gray-900">{datosEstudiante.ci}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Nombre Completo:</p>
                        <p className="font-medium text-gray-900">
                          {datosEstudiante.nombre} {datosEstudiante.apellido}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Fecha de Nacimiento:</p>
                        <p className="font-medium text-gray-900">{datosEstudiante.fecha_nacimiento}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Sexo:</p>
                        <p className="font-medium text-gray-900">
                          {datosEstudiante.sexo === 'M' ? 'Masculino' : 'Femenino'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resumen nivel y curso */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 space-y-2">
                    <h3 className="font-medium text-gray-900">Nivel y Curso</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-600">Nivel:</p>
                        <p className="font-medium text-gray-900">{datosNivel.nivel}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Curso:</p>
                        <p className="font-medium text-gray-900">
                          {cursos.find(c => c.value === datosNivel.curso_id)?.label || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Paralelo:</p>
                        <p className="font-medium text-gray-900">
                          {paralelos.find(p => p.value === datosNivel.paralelo_id)?.label?.split('(')[0].trim() || '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resumen tutores */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 space-y-2">
                    <h3 className="font-medium text-gray-900">Tutores Registrados ({tutores.length})</h3>
                    <div className="space-y-2">
                      {tutores.map((tutor, index) => (
                        <div key={tutor.id} className="text-sm bg-gray-50 p-2 rounded">
                          <p className="font-medium text-gray-900">
                            {tutor.nombre} {tutor.apellido}
                            {tutor.es_contacto_principal && (
                              <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                Contacto Principal
                              </span>
                            )}
                          </p>
                          <p className="text-gray-600">{tutor.relacion} - {tutor.email}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Botón para cargar documentos */}
                  <Button
                    onClick={() => setMostrarDocumentos(true)}
                    className="w-full"
                    variant="secondary"
                  >
                    Cargar Documentos
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Botones de navegación */}
        <div className="flex gap-4 justify-between">
          <Button
            onClick={irAlAnterior}
            disabled={paso === 1 || enviando}
            variant="secondary"
          >
            <ChevronLeft size={18} /> Anterior
          </Button>

          <div className="flex gap-4">
            {paso < 4 ? (
              <Button
                onClick={irAlSiguiente}
                disabled={cargando || enviando}
                variant="primary"
              >
                Siguiente <ChevronRight size={18} />
              </Button>
            ) : (
              <Button
                onClick={enviarFormulario}
                loading={enviando}
                variant="primary"
              >
                Registrar Estudiante
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modal de documentos */}
      <DocumentosModal
        isOpen={mostrarDocumentos}
        onClose={() => setMostrarDocumentos(false)}
        estudianteId={null}
        onDocumentosCargados={setDocumentosCargados}
      />
    </div>
  );
};

export default RegistroEstudianteForm;
